import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Mail, Lock, ArrowRight, Loader2 } from "lucide-react";

export default function LoginPage() {
  const { user, login, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const r = await login(email, password);
    if (!r.ok) setError(r.error);
    else navigate("/");
  };

  return (
    <div className="min-h-screen flex">
      {/* Left: Form */}
      <div className="w-full lg:w-1/2 bg-white flex flex-col justify-center items-center p-8 lg:p-16">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-12">
            <div className="w-9 h-9 rounded-md bg-brand grid place-items-center text-white font-bold font-heading">N</div>
            <div>
              <div className="font-heading text-lg font-bold tracking-tight leading-none">Neoventra ATS</div>
              <div className="text-xs text-gray-500 mt-0.5">Neoventra Inc.</div>
            </div>
          </div>

          <h1 className="text-3xl font-black tracking-tighter text-gray-900 mb-2">Welcome back</h1>
          <p className="text-sm text-gray-500 mb-10">Sign in to your control room.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="label-base" htmlFor="email">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10" />
                <input
                  data-testid="login-email-input"
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  className="block w-full rounded-md border-0 py-2.5 pl-10 pr-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand text-sm bg-white"
                  placeholder="admin@neoventra.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="label-base" htmlFor="password">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10" />
                <input
                  data-testid="login-password-input"
                  id="password"
                  type="password"
                  required
                  autoComplete="current-password"
                  className="block w-full rounded-md border-0 py-2.5 pl-10 pr-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand text-sm bg-white"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              className="btn-primary w-full"
              disabled={loading}
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <>Sign in <ArrowRight size={16} /></>}
            </button>
          </form>

          <div className="mt-10 text-xs text-gray-400 font-mono">
            v1.0 · internal use only
          </div>
        </div>
      </div>

      {/* Right: Hero image */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden bg-brand">
        <img
          src="https://images.unsplash.com/photo-1531972111231-7482a960e109?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80"
          alt="Modern glass office"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-brand/80 via-brand/40 to-black/40 mix-blend-multiply" />
        <div className="absolute bottom-0 left-0 right-0 p-12 text-white">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold opacity-80 mb-3">Applicant Tracking System</div>
          <h2 className="font-heading text-4xl font-black tracking-tighter leading-tight max-w-md">
            Hire smarter.<br />Move faster.
          </h2>
          <p className="text-sm opacity-80 mt-4 max-w-sm">
            One source of truth for leads, open positions, and candidate pipelines — built for Neoventra's recruitment team.
          </p>
        </div>
      </div>
    </div>
  );
}
